/**
 * Offline rehearsal entry for the EXISTING landing component, not a new page
 * implementation. Supplied cards use the existing server card renderer.
 */
import React from "react";
import { stripCanonicalTrailingSlash } from "../../../lib/copy/src/canonical-url.mjs";
import { hydrateRoot } from "react-dom/client";
import { createInstance } from "i18next";
import { I18nextProvider } from "react-i18next";
import { Router } from "wouter";
import MarketingSourceLanding from "../src/components/landing/MarketingSourceLanding";

export interface CandidateProps {
  path: string;
  language: string;
  canonical: string;
  title: string;
  source: {
    heading: string; subtitle: string; hero: { src: string; alt: string };
    sections: Array<Record<string, unknown>>;
  };
  labels: { search: string; placeholder: string; browse: string; inventory: string };
  cars: Array<{ id: number }>;
  cardHtml: string[];
  searchHref: string;
  fallbackLabel: string | null;
  candidateId: string;
}
export function candidateTree(props: CandidateProps) {
  const search = new URL(props.searchHref, "https://www.bookaclassic.com");
  if (search.searchParams.get("rental_type") === "mixed") search.searchParams.delete("rental_type");
  const searchHref = search.pathname + search.search;
  const i18n = createInstance();
  void i18n.init({
    lng: props.language, fallbackLng: props.language, initAsync: false,
    resources: { [props.language]: { translation: {
      "landing.browse_cars": props.labels.browse,
      "landing.no_cars_found": "",
      "landing.explore_more": props.language.startsWith("pt") ? "Explore mais opções" : props.labels.browse,
      "landing.related_pages": props.language.startsWith("pt") ? "Páginas relacionadas" : props.labels.browse,
    } } },
  });
  return <Router ssrPath={props.path}><I18nextProvider i18n={i18n}>
    <MarketingSourceLanding
      heading={props.source.heading} subtitle={props.source.subtitle}
      hero={props.source.hero} lang={props.language}
      sections={props.source.sections as never} introBlocks={null}
      inventoryHeading={props.labels.inventory}
      searchState={search.searchParams}
      cars={props.cars as never} loading={false} unavailable={false} fetching={false}
      onRetry={() => {}} browseLabel={props.labels.browse}
      heroAction={<form action={search.pathname} method="get" role="search"
        className="flex w-full max-w-xl flex-col gap-3 sm:flex-row">
        {[...search.searchParams.entries()].filter(([key]) => key !== "location")
          .map(([key, value]) => <input key={key} type="hidden" name={key} value={value} />)}
        <label htmlFor="release-nine-location" className="sr-only">{props.labels.placeholder}</label>
        <input id="release-nine-location" name="location" defaultValue={search.searchParams.get("location") || ""}
          placeholder={props.labels.placeholder}
          className="min-h-12 min-w-0 flex-1 rounded-full border bg-background px-5 py-3 text-foreground" />
        <button type="submit" className="primary-action min-h-12 rounded-full px-6 py-3 font-semibold">
          {props.labels.search}
        </button>
      </form>}
      inventoryFooter={<a href={searchHref}>{props.labels.browse}</a>}
      inventoryNotice={props.fallbackLabel
        ? <p data-landing-inventory-fallback="true">{props.fallbackLabel}</p> : null}
      renderCard={(_car, index) => <div key={props.cars[index].id}
        dangerouslySetInnerHTML={{ __html: props.cardHtml[index] }} />}
      seoSupplement={{ faqs: [], internalLinks: [] }}
      dataAttrs={{ "data-release-nine-rehearsal": props.candidateId }}
    />
  </I18nextProvider></Router>;
}
export function hydrateCandidateComponent() {
  const node = document.getElementById("release-nine-candidate-data");
  const root = document.getElementById("root");
  if (!node?.textContent || !root) throw new Error("Candidate hydration seed missing");
  const props = JSON.parse(node.textContent) as CandidateProps;
  if (!props.candidateId || !props.canonical.startsWith("https://www.bookaclassic.com/")) {
    throw new Error("Invalid candidate identity");
  }
  document.documentElement.lang = props.language;
  document.title = props.title;
  const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) throw new Error("Candidate canonical missing");
  canonical.href = stripCanonicalTrailingSlash(props.canonical);
  hydrateRoot(root, candidateTree(props), {
    onRecoverableError(error) {
      document.documentElement.dataset.rehearsalHydrationError = String(error);
    },
  });
  document.documentElement.dataset.rehearsalHydrated = props.candidateId;
}
